import { useEffect, useState } from 'react';
import { FormError } from '../components/FormError';
import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  Group,
  PasswordInput,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import {
  getAuthControllerMeQueryKey,
  useAuthControllerMe,
  useAuthControllerUpdateMe,
  useAuthControllerChangePassword,
} from '@werkbank/shared';
import { PageHeader } from '../components/PageHeader';
import { TotpCard } from '../components/TotpCard';
import { KalenderAboCard } from '../components/KalenderAboCard';
import { notify } from '../lib/notify';

/**
 * Selbstverwaltung des eigenen Profils (Nutzer-Menü → Profil). Jede angemeldete
 * Rolle bearbeitet ausschließlich den eigenen Datensatz über /auth/me. Zwei
 * getrennte Karten: Kontaktdaten und Passwortwechsel (aktuelles Passwort nötig).
 */
export function ProfilePage() {
  const me = useAuthControllerMe();
  const queryClient = useQueryClient();

  // ── Kontaktdaten ───────────────────────────────────────────────────────────
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [street, setStreet] = useState('');
  const [zip, setZip] = useState('');
  const [city, setCity] = useState('');

  // Formular aus den geladenen Daten befüllen (einmalig, wenn me eintrifft).
  const loadedId = me.data?.id;
  useEffect(() => {
    if (!me.data) return;
    setDisplayName(me.data.displayName ?? '');
    setEmail(me.data.email ?? '');
    setMobile(me.data.mobile ?? '');
    setStreet(me.data.privateStreet ?? '');
    setZip(me.data.privateZip ?? '');
    setCity(me.data.privateCity ?? '');
  }, [loadedId, me.data]);

  const updateMe = useAuthControllerUpdateMe({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getAuthControllerMeQueryKey() });
        notify.success('Profil gespeichert.');
      },
    },
  });

  const saveProfile = () =>
    updateMe.mutate({
      data: {
        displayName: displayName.trim(),
        email: email.trim(),
        mobile: mobile.trim(),
        privateStreet: street.trim(),
        privateZip: zip.trim(),
        privateCity: city.trim(),
      },
    });

  // ── Passwort ─────────────────────────────────────────────────────────────
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const changePassword = useAuthControllerChangePassword({
    mutation: {
      onSuccess: () => {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        notify.success('Passwort geändert. Andere Anmeldungen wurden beendet.');
      },
    },
  });

  const pwTooShort = newPassword.length > 0 && newPassword.length < 10;
  const pwMismatch = confirmPassword.length > 0 && confirmPassword !== newPassword;
  const canChangePassword =
    currentPassword.length > 0 && newPassword.length >= 10 && confirmPassword === newPassword;

  const submitPassword = () => {
    if (!canChangePassword) return;
    changePassword.mutate({ data: { currentPassword, newPassword } });
  };

  if (me.isLoading) return <PageHeader title="Profil" meta="lädt…" />;
  if (me.isError || !me.data) return <Text c="red">Profil konnte nicht geladen werden.</Text>;

  return (
    <Stack gap="lg">
      <PageHeader title="Profil" meta={`${me.data.displayName} · ${me.data.roleName}`} />

      {/* Kontaktdaten */}
      <Card padding={24}>
        <Stack gap="md">
          <Text fw={600} style={{ fontSize: 'var(--wb-font-size-lg)' }}>
            Persönliche Daten
          </Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput
              label="Name"
              value={displayName}
              onChange={(e) => setDisplayName(e.currentTarget.value)}
              data-testid="profile-name"
            />
            <TextInput
              label="E-Mail"
              type="email"
              description="Zugleich Ihr Anmeldename."
              inputWrapperOrder={['label', 'input', 'description', 'error']}
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              data-testid="profile-email"
            />
            <TextInput
              label="Handynummer"
              value={mobile}
              onChange={(e) => setMobile(e.currentTarget.value)}
              placeholder="z. B. 0151 23456789"
              data-testid="profile-mobile"
            />
          </SimpleGrid>

          <Text fw={600} size="sm" mt="xs">
            Private Adresse
          </Text>
          <SimpleGrid cols={{ base: 1, sm: 3 }}>
            <TextInput
              label="Straße und Hausnummer"
              value={street}
              onChange={(e) => setStreet(e.currentTarget.value)}
              data-testid="profile-street"
            />
            <TextInput
              label="PLZ"
              value={zip}
              onChange={(e) => setZip(e.currentTarget.value)}
              data-testid="profile-zip"
            />
            <TextInput
              label="Ort"
              value={city}
              onChange={(e) => setCity(e.currentTarget.value)}
              data-testid="profile-city"
            />
          </SimpleGrid>

          <FormError error={updateMe.error} />
          <Group justify="flex-end">
            <Button
              onClick={saveProfile}
              loading={updateMe.isPending}
              disabled={displayName.trim().length === 0 || email.trim().length === 0}
              data-testid="profile-save"
            >
              Speichern
            </Button>
          </Group>
        </Stack>
      </Card>

      {/* Passwort ändern */}
      <Card padding={24}>
        <Stack gap="md">
          <Text fw={600} style={{ fontSize: 'var(--wb-font-size-lg)' }}>
            Passwort ändern
          </Text>
          <SimpleGrid cols={{ base: 1, sm: 3 }}>
            <PasswordInput
              label="Aktuelles Passwort"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.currentTarget.value)}
              autoComplete="current-password"
              data-testid="profile-current-password"
            />
            <PasswordInput
              label="Neues Passwort"
              description="Mindestens 10 Zeichen."
              inputWrapperOrder={['label', 'input', 'description', 'error']}
              value={newPassword}
              onChange={(e) => setNewPassword(e.currentTarget.value)}
              error={pwTooShort ? 'Mindestens 10 Zeichen.' : undefined}
              autoComplete="new-password"
              data-testid="profile-new-password"
            />
            <PasswordInput
              label="Neues Passwort bestätigen"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.currentTarget.value)}
              error={pwMismatch ? 'Die Passwörter stimmen nicht überein.' : undefined}
              autoComplete="new-password"
              data-testid="profile-confirm-password"
            />
          </SimpleGrid>
          <FormError error={changePassword.error} />
          <Group justify="flex-end">
            <Button
              onClick={submitPassword}
              loading={changePassword.isPending}
              disabled={!canChangePassword}
              data-testid="profile-change-password"
            >
              Passwort ändern
            </Button>
          </Group>
        </Stack>
      </Card>

      {/* Zwei-Faktor-Anmeldung (TOTP) — gehärtete Auth-Endpoints, s. TotpCard. */}
      <TotpCard totpEnabled={me.data.totpEnabled} />

      {/* Faruq 2026-10-30: Kalender-Abo der Plantafel — vorerst nur Administratoren. */}
      {me.data.roleIsAdmin && <KalenderAboCard />}
    </Stack>
  );
}
